import React, { useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogContent,
  IconButton, InputAdornment, TextField, Typography,
} from "@mui/material";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import axios from "axios";
import { toast } from "react-toastify";
import { api_url } from "../../apiUtils";
import { useAppStore } from "../../appStore";

export default function ForcedPasswordChangeDialog() {
  const { profileData, setProfileData } = useAppStore();
  const [step, setStep] = useState(1);
  const [mobileNumber, setMobileNumber] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const open = Boolean(profileData?.mustChangePassword);
  const headers = { accesstoken: sessionStorage.getItem("accesstoken") };

  const verifyMobile = async () => {
    if (!/^\d{10}$/.test(mobileNumber)) {
      setError("Enter your registered 10-digit mobile number.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(
        `${api_url}auth/verify-temporary-password-mobile`,
        { mobileNumber },
        { headers },
      );
      setStep(2);
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to verify mobile number.");
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async () => {
    if (newPassword.length < 6) {
      setError("Password must contain at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(
        `${api_url}auth/change-temporary-password`,
        { mobileNumber, newPassword },
        { headers },
      );
      setProfileData({ ...profileData, mustChangePassword: false });
      toast.success("Password updated. Your account is ready.");
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to update password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="xs"
      disableEscapeKeyDown
      PaperProps={{ sx: { borderRadius: 3.5, overflow: "hidden", boxShadow: "0 28px 80px rgba(23,27,68,.32)" } }}
    >
      <Box sx={{ p: 3, textAlign: "center", color: "white", background: "linear-gradient(145deg,#171b44,#416da8)" }}>
        <Box sx={{ mx: "auto", mb: 1.25, width: 58, height: 58, borderRadius: "50%", display: "grid", placeItems: "center", backgroundColor: "rgba(255,255,255,.14)" }}>
          <LockResetRoundedIcon sx={{ fontSize: 34 }} />
        </Box>
        <Typography sx={{ fontSize: 21, fontWeight: 900 }}>Secure Your Account</Typography>
        <Typography sx={{ mt: 0.6, fontSize: 13, color: "rgba(255,255,255,.76)" }}>
          Please update your temporary password to continue.
        </Typography>
      </Box>
      <DialogContent sx={{ p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {step === 1 ? (
          <>
            <Typography sx={{ mb: 1.5, color: "#667085", fontSize: 13.5 }}>
              First, confirm the mobile number registered with this profile.
            </Typography>
            <TextField
              autoFocus fullWidth label="Registered Mobile Number" value={mobileNumber}
              onChange={(event) => { setMobileNumber(event.target.value.replace(/\D/g, "").slice(0, 10)); setError(""); }}
              inputProps={{ inputMode: "numeric", maxLength: 10 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIphoneRoundedIcon /></InputAdornment> }}
            />
            <Button fullWidth variant="contained" onClick={verifyMobile} disabled={loading} sx={{ mt: 2, py: 1.2, borderRadius: 2, fontWeight: 800, textTransform: "none", background: "linear-gradient(135deg,#1e2466,#416da8)" }}>
              {loading ? <CircularProgress size={22} color="inherit" /> : "Verify Mobile Number"}
            </Button>
          </>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, color: "#27845a" }}>
              <CheckCircleOutlineRoundedIcon />
              <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>Mobile number verified</Typography>
            </Box>
            <Box sx={{ display: "grid", gap: 2 }}>
              <TextField fullWidth label="New Password" type={showPassword ? "text" : "password"} value={newPassword} onChange={(event) => { setNewPassword(event.target.value); setError(""); }} InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword((value) => !value)}>{showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}</IconButton></InputAdornment> }} />
              <TextField fullWidth label="Confirm New Password" type={showPassword ? "text" : "password"} value={confirmPassword} onChange={(event) => { setConfirmPassword(event.target.value); setError(""); }} />
            </Box>
            <Button fullWidth variant="contained" onClick={updatePassword} disabled={loading} sx={{ mt: 2.25, py: 1.2, borderRadius: 2, fontWeight: 800, textTransform: "none", background: "linear-gradient(135deg,#1e2466,#416da8)" }}>
              {loading ? <CircularProgress size={22} color="inherit" /> : "Update Password & Continue"}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
